import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import LockResetIcon from '@mui/icons-material/LockReset';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { useState, useContext } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import ResetPasswordChecks from './ResetPasswordChecks';
import { getServerURL } from '../../utils';
import { ClinicRxContext } from '../../App';

const SERVER_URL = getServerURL();
const MIN_LENGTH = 8;
const MAX_LENGTH = 32;

export default function ResetPasswordPopover({
  popoverAnchor,
  handleClosePopover,
  resetPassword,
  setResetPassword,
}) {
  const {
    state: {
      user: { chatID },
    },
    fetchProtected,
  } = useContext(ClinicRxContext);

  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const checks = {
    isLengthOkay:
      resetPassword.length >= MIN_LENGTH && resetPassword.length <= MAX_LENGTH,
    includesUppercase: /[A-Z]/.test(resetPassword),
    includesLowercase: /[a-z]/.test(resetPassword),
    containsNumber: /[0-9]/.test(resetPassword),
    containsSpecialChars: /[^A-Za-z0-9\s]/.test(resetPassword),
  };

  const allChecksPass = Object.values(checks).every(Boolean);

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleReset = (message) => {
    setResetPassword('');
    handleClosePopover();
    handleCloseDialog();
    setIsLoading(false);
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  };

  return (
    <>
      <Popover
        id={popoverAnchor ? 'reset-password' : undefined}
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '5px',
            }}
          >
            <TextField
              id='reset-password'
              label='Reset Password'
              variant='outlined'
              type='text'
              value={resetPassword}
              onChange={(e) => {
                setResetPassword(e.target.value);
              }}
            />
            <IconButton
              disabled={!allChecksPass}
              aria-label='submit'
              color='primary'
              sx={{ alignSelf: 'center' }}
            >
              <LockResetIcon
                onClick={() => {
                  allChecksPass && setShowDialog(true);
                }}
              />
            </IconButton>
          </Box>
          <ResetPasswordChecks
            isLengthOkay={checks.isLengthOkay}
            lengthRules={{ max: MAX_LENGTH, min: MIN_LENGTH }}
            includesUppercase={checks.includesUppercase}
            includesLowercase={checks.includesLowercase}
            containsNumber={checks.containsNumber}
            containsSpecialChars={checks.containsSpecialChars}
          />
        </Box>
      </Popover>
      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
      >
        <Box
          sx={{
            height: '30vh',
            padding: '10vh',
            display: 'flex',
            flexDirection: 'column',
            gap: '2vh',
          }}
        >
          <DialogTitle>Confirm password reset</DialogTitle>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress aria-label='Loading…' />
            </Box>
          ) : (
            <Button
              variant='contained'
              onClick={async () => {
                setIsLoading(true);
                const response = await fetchProtected((bearerToken) =>
                  fetch(`${SERVER_URL}/reset-password`, {
                    method: 'POST',
                    headers: {
                      'content-type': 'application/json',
                      authorization: bearerToken,
                    },
                    body: JSON.stringify({
                      userID: chatID,
                      edit: {
                        password: resetPassword,
                      },
                    }),
                  }),
                );
                const { message } = await response.json();
                handleReset(message);
              }}
            >
              OK
            </Button>
          )}
        </Box>
      </Dialog>
      <Snackbar
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isSnackbarOpen}
        onClose={() => {
          setIsSnackbarOpen(false);
          setSnackbarMessage('');
        }}
        key='top-right'
      >
        <Alert
          severity={snackbarMessage.includes('success') ? 'success' : 'error'}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
